/**
 * MaxBridge — IPlatformBridge implementation for MAX Messenger WebApp
 * Documentation: https://dev.max.ru/docs/webapps/bridge
 */

import type { IPlatformBridge, HapticStyle, HapticNotificationType } from './types';
import type { PlatformType, PlatformUserDTO, PlatformThemeParamsDTO } from '@tg-miniapp/shared-types';

declare global {
  interface Window {
    WebApp?: {
      initData: string;
      initDataUnsafe: {
        query_id?: string;
        user?: {
          id: number;
          first_name: string;
          last_name?: string;
          username?: string;
          language_code?: string;
          photo_url?: string;
        };
        chat?: {
          id: number;
          type: 'DIALOG' | 'CHAT' | 'CHANNEL';
        };
        start_param?: string;
        auth_date?: number;
        hash?: string;
      };
      version?: string;
      platform?: 'ios' | 'android' | 'desktop' | 'web';
      colorScheme?: 'light' | 'dark';
      themeParams?: any;
      ready: () => void;
      expand: () => void;
      close: () => void;
      enableClosingConfirmation?: () => void;
      disableClosingConfirmation?: () => void;
      openLink?: (url: string) => void;
      openMaxLink?: (url: string) => void;
      requestContact?: () => Promise<{ phone: string; authDate: number; hash: string }>;
      requestScreenMaxBrightness?: () => void;
      restoreScreenBrightness?: () => void;
      ScreenCapture?: {
        enableScreenCapture: () => void;
        disableScreenCapture: () => void;
      };
    };
  }
}

export class MaxBridge implements IPlatformBridge {
  readonly platform: PlatformType = 'max';

  private get max(): any {
    return typeof window !== 'undefined' ? window.WebApp : undefined;
  }

  get isAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.WebApp;
  }

  get initData(): string {
    let resolved = this.max?.initData || '';

    if (!resolved && typeof window !== 'undefined') {
      try {
        const rawHash = window.location.hash.slice(1);
        const hashParams = new URLSearchParams(rawHash);
        const maxWebAppData = hashParams.get('WebAppData');
        if (maxWebAppData) {
          resolved = maxWebAppData;
        } else if (rawHash.startsWith('WebAppData=')) {
          const ampersandIdx = rawHash.indexOf('&');
          resolved = ampersandIdx !== -1 
            ? rawHash.slice('WebAppData='.length, ampersandIdx) 
            : rawHash.slice('WebAppData='.length);
          resolved = decodeURIComponent(resolved);
        }

        if (!resolved) {
          const searchParams = new URLSearchParams(window.location.search);
          const searchData = searchParams.get('WebAppData');
          if (searchData) resolved = searchData;
        }
      } catch {}
    }

    if (resolved) {
      try {
        sessionStorage.setItem('max_init_data_backup', resolved);
      } catch {}
      return resolved;
    }

    try {
      const cachedBackup = sessionStorage.getItem('max_init_data_backup');
      if (cachedBackup) return cachedBackup;
    } catch {}

    return '';
  }

  get user(): PlatformUserDTO | null {
    const rawUser = this.max?.initDataUnsafe?.user;
    if (rawUser) {
      return {
        id: `max:${rawUser.id}`,
        rawId: rawUser.id,
        platform: 'max',
        firstName: rawUser.first_name || 'Пользователь MAX',
        lastName: rawUser.last_name,
        username: rawUser.username,
        languageCode: rawUser.language_code,
        photoUrl: rawUser.photo_url
      };
    }
    return null;
  }

  get startParam(): string | undefined {
    if (this.max?.initDataUnsafe?.start_param) {
      return this.max.initDataUnsafe.start_param;
    }
    if (typeof window !== 'undefined') {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const fromSearch = searchParams.get('startapp') || searchParams.get('start');
        if (fromSearch) return fromSearch;

        const rawHash = window.location.hash.slice(1);
        const hashParams = new URLSearchParams(rawHash);
        const fromHash = hashParams.get('startapp') || hashParams.get('start');
        if (fromHash) return fromHash;

        const webAppData = hashParams.get('WebAppData') || searchParams.get('WebAppData');
        if (webAppData) {
          const decoded = decodeURIComponent(webAppData);
          const dataParams = new URLSearchParams(decoded);
          const fromData = dataParams.get('start_param') || dataParams.get('startapp');
          if (fromData) return fromData;
        }
      } catch {}
    }
    return undefined;
  }

  get colorScheme(): 'light' | 'dark' {
    return this.max?.colorScheme === 'dark' ? 'dark' : 'light';
  }

  get themeParams(): PlatformThemeParamsDTO {
    const tp = this.max?.themeParams;
    return {
      bgColor: tp?.bg_color || tp?.background_color,
      textColor: tp?.text_color,
      hintColor: tp?.hint_color,
      linkColor: tp?.link_color,
      buttonColor: tp?.button_color,
      buttonTextColor: tp?.button_text_color,
      secondaryBgColor: tp?.secondary_bg_color
    };
  }

  ready(): void {
    try {
      if (this.max?.transport && typeof window !== 'undefined') {
        const win = window as any;
        if (win.WebViewHandler && this.max.transport.sendEvent !== this.max.transport.sendOverWebView) {
          this.max.transport.webviewBridge = win.WebViewHandler;
          this.max.transport.sendEvent = this.max.transport.sendOverWebView;
        }
      }
      this.max?.ready?.();
    } catch {}

    // Прямой вызов нативных обработчиков iOS WebKit для гарантированного снятия сплеш-оверлея MAX
    if (typeof window !== 'undefined') {
      const win = window as any;
      try {
        win.WebViewHandler?.postEvent?.('WebAppReady', '{}');
      } catch {}
      try {
        win.webkit?.messageHandlers?.WebViewHandler?.postMessage?.(JSON.stringify({ type: 'WebAppReady' }));
        win.webkit?.messageHandlers?.webAppReady?.postMessage?.('{}');
      } catch {}
      try {
        window.parent?.postMessage?.(JSON.stringify({ type: 'WebAppReady' }), '*');
      } catch {}
    }
  }

  expand(): void {
    try {
      this.max?.expand?.();
    } catch {}
    if (typeof window !== 'undefined') {
      const win = window as any;
      try {
        win.WebViewHandler?.postEvent?.('WebAppExpand', '{}');
      } catch {}
      try {
        win.webkit?.messageHandlers?.WebViewHandler?.postMessage?.(JSON.stringify({ type: 'WebAppExpand' }));
        win.webkit?.messageHandlers?.webAppExpand?.postMessage?.('{}');
      } catch {}
      try {
        window.parent?.postMessage?.(JSON.stringify({ type: 'WebAppExpand' }), '*');
      } catch {}
    }
  }

  close(): void {
    try {
      this.max?.close?.();
    } catch {}
  }

  showBackButton(onClick: () => void): () => void {
    // В MAX управление навигацией может осуществляться через нативный стек либо историю браузера
    const handlePopState = () => {
      onClick();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }

  hapticImpact(_style: HapticStyle = 'light'): void {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(15);
      }
    } catch {}
  }

  hapticNotification(type: HapticNotificationType): void {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        if (type === 'error') navigator.vibrate([30, 50, 30]);
        else if (type === 'success') navigator.vibrate([20, 30, 20]);
        else navigator.vibrate(25);
      }
    } catch {}
  }

  hapticSelection(): void {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(10);
      }
    } catch {}
  }

  openLink(url: string, _options?: { tryInstantView?: boolean }): void {
    if (this.max?.openMaxLink && url.startsWith('https://max.ru/')) {
      this.max.openMaxLink(url);
    } else if (this.max?.openLink) {
      this.max.openLink(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  shareContent(text: string, _url?: string): void {
    const shareUrl = `https://max.ru/:share?text=${encodeURIComponent(text)}`;
    this.openLink(shareUrl);
  }

  enableClosingConfirmation(): void {
    try {
      this.max?.enableClosingConfirmation?.();
    } catch {}
  }

  disableClosingConfirmation(): void {
    try {
      this.max?.disableClosingConfirmation?.();
    } catch {}
  }

  async requestContact(): Promise<{ phone: string } | null> {
    if (this.max?.requestContact) {
      try {
        const res = await this.max.requestContact();
        return { phone: res.phone };
      } catch {
        return null;
      }
    }
    return null;
  }

  requestScreenMaxBrightness(): void {
    try {
      this.max?.requestScreenMaxBrightness?.();
    } catch {}
  }

  restoreScreenBrightness(): void {
    try {
      this.max?.restoreScreenBrightness?.();
    } catch {}
  }
}
